import { MutationTree } from 'vuex';
import { ISetDocumentRevisionStatusPayload } from '../types/set-document-revision-status-payload';
import { IPreviewToken } from '@/document-edit/assembler/document/types/preview-token';
import { EDocumentViewMutation } from '@/document-view/store/document-view.constants';
import { IDocumentViewState } from '@/document-view/store/document-view.types';
import { IDocumentEventItem } from '@/document-view/types/document-events-item';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { IDocumentRevision } from '@lib-shared/types/document-revision';
import { IDocumentNotification } from '@lib-shared/types/DocumentNotification';
import { ITag } from '@lib-shared/types/Tag';
import { IRecipientURL } from '@/api/recipients/types/recipient-url';
import { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';

export const mutations: MutationTree<IDocumentViewState> = {
  [EDocumentViewMutation.SetDocument](state: IDocumentViewState, payload: IDocument) {
    state.document = payload;
  },
  [EDocumentViewMutation.SetLoading](state: IDocumentViewState, loading: boolean) {
    state.loading = loading;
  },
  [EDocumentViewMutation.SetLoaded](state: IDocumentViewState, loaded: boolean) {
    state.loaded = loaded;
  },
  [EDocumentViewMutation.SetState](state: IDocumentViewState, newState: IDocumentViewState) {
    Object.assign(state, newState);
  },
  [EDocumentViewMutation.SetPreviewToken](state: IDocumentViewState, previewToken: IPreviewToken) {
    state.previewToken = previewToken;
  },
  [EDocumentViewMutation.SetTags](state: IDocumentViewState, tags: ITag[]) {
    state.document.tags = tags;
  },
  [EDocumentViewMutation.SetDocumentEvents](
    state: IDocumentViewState,
    events: IDocumentEventItem[]
  ) {
    state.events = events;
  },
  [EDocumentViewMutation.SetDocumentNotFound](state: IDocumentViewState, value: boolean) {
    state.documentNotFound = value;
  },
  [EDocumentViewMutation.SetDocument](state: IDocumentViewState, document: IDocument) {
    state.document = { ...document };
  },
  [EDocumentViewMutation.SetDocumentRevisions](
    state: IDocumentViewState,
    documentRevisions: IDocumentRevision[]
  ) {
    state.document.documentRevisions = documentRevisions;
  },
  [EDocumentViewMutation.SetDocumentRevisionStatus](
    state: IDocumentViewState,
    payload: ISetDocumentRevisionStatusPayload
  ) {
    state.document.documentRevisions = state.document.documentRevisions.map(
      (documentRevision: IDocumentRevision) =>
        documentRevision.revision === payload.revision
          ? { ...documentRevision, status: payload.status }
          : documentRevision
    );
  },
  [EDocumentViewMutation.SetRevisionsLoading](state: IDocumentViewState, loading: boolean) {
    state.revisionsLoading = loading;
  },
  [EDocumentViewMutation.SetUpdateFailed](state: IDocumentViewState) {
    state.updateFailed = true;
  },
  [EDocumentViewMutation.SetPages](state: IDocumentViewState, pages: IDocumentPage[]) {
    state.document.pages = pages;
  },
  [EDocumentViewMutation.SetDocumentNotifications](
    state: IDocumentViewState,
    notifications: IDocumentNotification[]
  ) {
    state.notifications = notifications;
  },
  [EDocumentViewMutation.SetDismissedEditPromptBar](state: IDocumentViewState) {
    state.dismissedEditPromptBar = true;
  },
  [EDocumentViewMutation.SetRecipientURLs](
    state: IDocumentViewState,
    recipientURLs: IRecipientURL[]
  ) {
    state.recipientURLs = recipientURLs;
  },
  [EDocumentViewMutation.SetLoadingRecipientLinks](state: IDocumentViewState, loading: boolean) {
    state.loadingRecipientLinks = loading;
  },
  [EDocumentViewMutation.SetLoadedRecipientLinks](state: IDocumentViewState, loaded: boolean) {
    state.loadedRecipientLinks = loaded;
  },
  [EDocumentViewMutation.SetEditorRecipientInput](
    state: IDocumentViewState,
    editorRecipientInput: IEditorRecipientInput[]
  ) {
    state.editorRecipientInput = editorRecipientInput;
  },
};
